import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Package, ArrowRight } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { trackPurchase } from '../utils/analytics';
import { trackMetaPurchase } from '../utils/metaPixel';

const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const freeOrderId = searchParams.get('order_id');
  const { clearCart } = useCart();
  
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (status !== 'success' || trackedRef.current) return;
    trackedRef.current = true;
    // Try to fetch the actual order details for richer purchase event
    (async () => {
      try {
        const orderId = freeOrderId || paymentData?.order_id || paymentData?.metadata?.order_id;
        let order = null;
        if (orderId) {
          try {
            const res = await api.get(`/orders/${orderId}`);
            order = res.data;
          } catch (err) {
            console.debug('[purchase] order fetch failed, using cart fallback:', err?.message);
          }
        }

        // Guard against duplicate GA4/Ads events on page refresh: useRef
        // resets on every mount, but localStorage survives F5, tab close and
        // even revisits days later (e.g. from an emailed order confirmation
        // link). Once a transaction_id has been tracked we never fire
        // purchase / conversion / meta-purchase for it again.
        const stableTxnId = String(order?.order_id || orderId || sessionId || '');
        const trackedKey = stableTxnId ? `ga4_purchase_tracked_${stableTxnId}` : '';
        if (trackedKey) {
          try {
            if (localStorage.getItem(trackedKey)) {
              console.debug('[purchase] already tracked this transaction, skipping', stableTxnId);
              return;
            }
          } catch (_) { /* private-mode / Safari: fall through and re-fire */ }
        }

        // FIX #1: Compute Enhanced Conversions user_data BEFORE any events so
        // that GA4 purchase, Meta Purchase and Google Ads conversion all attach it.
        // FIX #2: Default country to 'SE' if shipping_address doesn't include it
        // (Google Ads requires country for address-based Enhanced Conversions matching).
        // FIX #3: Only fire the conversion when value > 0 — Google Ads flags
        // zero-value conversions as "Misconfigured".
        const ship = order?.shipping_address || {};
        const userData = {};
        const customerEmail = order?.email || order?.customer_email;
        if (customerEmail) userData.email = String(customerEmail).trim().toLowerCase();
        const phone = ship.phone || order?.phone;
        if (phone) {
          const digits = String(phone).replace(/\D/g, '');
          if (digits) userData.phone_number = digits.startsWith('46') ? `+${digits}` : (digits.startsWith('0') ? `+46${digits.slice(1)}` : `+${digits}`);
        }
        const address = {};
        if (ship.first_name) address.first_name = String(ship.first_name).trim().toLowerCase();
        if (ship.last_name) address.last_name = String(ship.last_name).trim().toLowerCase();
        if (ship.address || ship.street) address.street = String(ship.address || ship.street).trim().toLowerCase();
        if (ship.city) address.city = String(ship.city).trim().toLowerCase();
        if (ship.postal_code) address.postal_code = String(ship.postal_code).replace(/\s/g, '');
        address.country = String(ship.country || 'SE').toUpperCase().slice(0, 2);
        if (Object.keys(address).length > 1) userData.address = address; // >1 because country always set

        if (typeof window !== 'undefined' && Object.keys(userData).length > 0 && typeof window.gtag === 'function') {
          // Set user_data globally BEFORE any events so every subsequent
          // conversion attaches Enhanced Conversion data.
          window.gtag('set', 'user_data', userData);
        }

        trackPurchase(order || {
          order_id: orderId || sessionId,
          total_amount: (paymentData?.amount_total || 0) / 100,
          items: [],
        });
        trackMetaPurchase(order || {
          order_id: orderId || sessionId,
          total_amount: (paymentData?.amount_total || 0) / 100,
          items: [],
        });

        // Persist the "tracked" flag AFTER events fire so a mid-flight crash
        // doesn't leave us unable to retry. localStorage survives tab close
        // so refresh-spam or later revisits (from an email link) don't duplicate.
        if (trackedKey) {
          try { localStorage.setItem(trackedKey, String(Date.now())); } catch (_) { /* ignore quota / private-mode */ }
        }

        // Google Customer Reviews opt-in badge — asks the customer if we may
        // email them a review request 1–14 days after estimated delivery. The
        // script is inserted only after we have real order data (order_id + email).
        try {
          const gcrEmail = order?.email || order?.customer_email;
          const gcrOrderId = String(order?.order_id || orderId || sessionId || '').slice(0, 60);
          if (gcrEmail && gcrOrderId && !document.getElementById('gcr-optin-script')) {
            // Estimated delivery date: 5 business days from today (matches our stated 3–7 day range).
            const eta = new Date();
            let added = 0;
            while (added < 5) {
              eta.setDate(eta.getDate() + 1);
              const day = eta.getDay();
              if (day !== 0 && day !== 6) added += 1;
            }
            const yyyyMmDd = eta.toISOString().slice(0, 10);
            window.renderOptIn = function () {
              window.gapi.load('surveyoptin', function () {
                window.gapi.surveyoptin.render({
                  merchant_id: 5762393056,
                  order_id: gcrOrderId,
                  email: String(gcrEmail).trim().toLowerCase(),
                  delivery_country: 'SE',
                  estimated_delivery_date: yyyyMmDd,
                });
              });
            };
            const s = document.createElement('script');
            s.id = 'gcr-optin-script';
            s.src = 'https://apis.google.com/js/platform.js?onload=renderOptIn';
            s.async = true;
            s.defer = true;
            document.body.appendChild(s);
          }
        } catch (err) {
          console.debug('[gcr] failed to init review opt-in:', err?.message);
        }
        // Google Ads conversion event with Enhanced Conversions (user_data already set globally above)
        try {
          const txnId = String(order?.order_id || orderId || sessionId || '');
          const value = Number(order?.total_amount || (paymentData?.amount_total || 0) / 100) || 0;
          // FIX: Skip Ads conversion for zero-value orders (free catalog etc.) —
          // Google Ads marks these as "Misconfigured" and they pollute conversion stats.
          if (value > 0 && txnId && typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'conversion',
              send_to: 'AW-11158311178/WY4wCMzv6NMZEIqi2cgp',
              value,
              currency: 'SEK',
              transaction_id: txnId,
            });
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'conversion', {
                send_to: 'AW-11158311178/WY4wCMzv6NMZEIqi2cgp',
                value,
                currency: 'SEK',
                transaction_id: txnId,
              });
            }
            console.debug('[ads-conversion] fired', { txnId, value, enhanced: Object.keys(userData) });
          } else {
            console.debug('[ads-conversion] skipped (zero-value or missing txn_id)', { txnId, value });
          }
        } catch (err) {
          console.debug('[ads-conversion] tracking failed:', err?.message);
        }
      } catch (err) {
        // never break the page
        console.debug('[purchase] tracking failed:', err?.message);
      }
    })();
  }, [status, freeOrderId, paymentData, sessionId]);

  useEffect(() => {
    // Free order — already completed, no Stripe check needed
    if (freeOrderId) {
      setStatus('success');
      setPaymentData({ payment_status: 'paid', amount_total: 0, currency: 'sek' });
      try { clearCart(); } catch (err) { console.error('clearCart failed after free order:', err); }
      return;
    }

    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        setPaymentData(response.data);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Clear cart after successful payment
          await clearCart();
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          // Continue polling
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        if (attempts < 3) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, freeOrderId, attempts, clearCart]);

  const formatAmount = (amount) => {
    if (!amount) return '0';
    // Amount is in cents
    return (amount / 100).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12" data-testid="order-confirmation-page">
      <div className="container-main max-w-2xl">
        {status === 'loading' && (
          <div className="bg-white rounded-xl p-12 shadow-soft text-center">
            <div className="spinner mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-slate-700">Kontrollerar betalning...</h2>
            <p className="text-slate-500 mt-2">Vänta medan vi bekräftar din beställning</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-xl p-12 shadow-soft text-center animate-slide-up">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="success-title">
              Tack för din beställning!
            </h1>
            <p className="text-slate-600 mb-8">
              Din betalning har genomförts. En orderbekräftelse har skickats till din e-post.
            </p>
            
            {paymentData && (
              <div className="bg-slate-50 rounded-lg p-6 mb-8 text-left">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Orderdetaljer
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Betalningsstatus</span>
                    <span className="text-green-600 font-medium">Betald</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Summa</span>
                    <span className="font-medium">{formatAmount(paymentData.amount_total)} {paymentData.currency?.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Session ID</span>
                    <span className="font-mono text-xs truncate max-w-[200px]">{sessionId}</span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/konto">
                <Button variant="outline" className="btn-outline" data-testid="view-orders">
                  Se dina beställningar
                </Button>
              </Link>
              <Link to="/produkter">
                <Button className="btn-primary" data-testid="continue-shopping">
                  Fortsätt handla
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === 'pending' && (
          <div className="bg-white rounded-xl p-12 shadow-soft text-center">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="w-10 h-10 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Betalning bearbetas
            </h1>
            <p className="text-slate-600 mb-6">
              Din betalning bearbetas. Du kommer att få en bekräftelse via e-post när den är klar.
            </p>
            <Link to="/">
              <Button className="btn-primary">
                Tillbaka till startsidan
              </Button>
            </Link>
          </div>
        )}

        {status === 'expired' && (
          <div className="bg-white rounded-xl p-12 shadow-soft text-center">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-orange-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Betalningssessionen har gått ut
            </h1>
            <p className="text-slate-600 mb-6">
              Din betalningssession har löpt ut. Försök igen.
            </p>
            <Link to="/varukorg">
              <Button className="btn-primary">
                Tillbaka till varukorgen
              </Button>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-xl p-12 shadow-soft text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Något gick fel
            </h1>
            <p className="text-slate-600 mb-6">
              Vi kunde inte verifiera din betalning. Kontakta kundtjänst om problemet kvarstår.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/varukorg">
                <Button variant="outline" className="btn-outline">
                  Tillbaka till varukorgen
                </Button>
              </Link>
              <Link to="/">
                <Button className="btn-primary">
                  Till startsidan
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderConfirmation;
